import {Component, EventEmitter, inject, Input, OnInit, Output, viewChild} from '@angular/core';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell, MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatNoDataRow,
    MatRow, MatRowDef, MatTable
} from '@angular/material/table';
import {MatRipple} from '@angular/material/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ErrorDetailPreview} from '../../../models/error/ErrorDetailPreview';
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonChip,
    IonIcon,
    IonSearchbar,
    IonSpinner,
    IonModal,
    IonContent,
    IonHeader,
    IonButtons,
    IonToolbar,
    IonTitle,
    IonAlert
} from '@ionic/angular/standalone';
import {ErrorPriority} from '../../../enums/ErrorPriority';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {BaseError} from '../../../models/error/BaseError';
import {NgComponentOutlet} from '@angular/common';
import {ExceptionDetailRecordComponent} from '../../exception-detail-record/exception-detail-record.component';
import {
    ErrorMessageDetailRecordComponent
} from '../../error-message-detail-record/error-message-detail-record.component';
import {LogDetailRecordComponent} from '../../log-detail-record/log-detail-record.component';
import {MatPaginator, MatPaginatorIntl, PageEvent} from '@angular/material/paginator';
import {CustomPaginatorIntl} from '../../../models/materialComponent/CustomPaginatorIntl';
import { PagedResult } from '../../../models/PagedResult';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { LogBookErrorFilter } from '../../../models/filter/LogBookErrorFilter';
import { LogBookQueryFilter } from '../../../models/filter/LogBookQueryFilter';
import { ConfirmationDialogComponent } from '../dialogs/confirmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'app-log-book-error-list',
    templateUrl: './log-book-error-list.component.html',
    styleUrls: ['./log-book-error-list.component.css'],
    standalone: true,
    providers: [{provide: MatPaginatorIntl, useClass: CustomPaginatorIntl}],
    imports: [
        MatTable,
        MatHeaderRow,
        MatRow,
        MatHeaderCell,
        MatCell,
        MatColumnDef,
        MatHeaderCellDef,
        MatCellDef,
        MatRowDef,
        MatNoDataRow,
        MatHeaderRowDef,
        MatRipple,
        TranslatePipe,
        IonChip,
        IonCard,
        IonCardContent,
        IonCardHeader,
        IonCardTitle,
        IonIcon,
        IonButton,
        NgComponentOutlet,
        MatPaginator,
        IonSpinner, 
        IonSearchbar, 
        IonModal, 
        IonContent, 
        IonButtons, 
        IonHeader,
        IonToolbar, 
        IonTitle,
        ReactiveFormsModule,
        IonAlert,
        ConfirmationDialogComponent
    ],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class LogBookErrorListComponent implements OnInit {
    private static readonly MAX_LENGTH_ERROR_CONTENT: number = 60;

    protected readonly columnsToDisplay: string[] = ['errorType', 'priority', 'errorContent', 'firstOccurred', 'mostRecentOccurred', 'isSolved', 'expand'];
    protected readonly errorTypes: Map<ErrorPriority, string> = new Map<ErrorPriority, string>([
        ["error", "error.type.error"],
        ["warning", "error.type.warning"],
        ["information", "error.type.information"],
    ]);

    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly formBuilder = inject(FormBuilder);

    public filterForm = this.formBuilder.group({
        search: this.formBuilder.nonNullable.control<string>('')
    });

    private filterModal = viewChild<IonModal>('filterModal');

    @Input({required: true}) isLoading: boolean = false;
    @Input({required: true}) errorsPagedResult!: PagedResult<ErrorDetailPreview>;
    @Input({required: true}) pageSizeOptions!: number[];
    @Input({required: true}) queryFilter!: LogBookQueryFilter;

    protected expandedError?: ErrorDetailPreview;
    protected lastClosedError?: ErrorDetailPreview;

    @Output() allErrorsSolved: EventEmitter<void> = new EventEmitter<void>();
    @Output() queryFilterChanged: EventEmitter<LogBookQueryFilter> = new EventEmitter<LogBookQueryFilter>();

    ngOnInit() {
    }

    public get lengthErrorContent() {
        return LogBookErrorListComponent.MAX_LENGTH_ERROR_CONTENT;
    }

    protected getErrorPriorityDescription(priority: ErrorPriority): string {
        const translationKey = this.errorTypes.get(priority) ?? "error.type.unknown";
        return this.translateService.instant(translationKey);
    }

    protected getErrorType(error: ErrorDetailPreview): string {
        let translationKey: string;
        if (error.errorType == "errorMessage" && error.priority == "information")
            translationKey = "error.name.infoErrorMessage";
        else if (error.errorType == "errorMessage")
            translationKey = "error.name.generalErrorMessage";
        else if (error.errorType == "exception")
            translationKey = "error.name.exception";
        else
            translationKey = "error.name.log";

        return this.translateService.instant(translationKey);
    }

    protected isExpanded(errorDetailPreview: ErrorDetailPreview) {
        return this.expandedError == errorDetailPreview;
    }

    protected isLastClosedError(errorDetailPreview: ErrorDetailPreview) {
        return this.lastClosedError != undefined && this.lastClosedError == errorDetailPreview;
    }

    protected toggle(errorDetailPreview: ErrorDetailPreview) {
        this.lastClosedError = this.expandedError;
        this.expandedError = this.isExpanded(errorDetailPreview) ? undefined : errorDetailPreview;
    }

    protected getDetailsRecordComponent(errorDetailPreview: ErrorDetailPreview) {
        if (errorDetailPreview.errorType == "exception") {
            return ExceptionDetailRecordComponent;
        } else if (errorDetailPreview.errorType == "errorMessage") {
            return ErrorMessageDetailRecordComponent;
        } else {
            return LogDetailRecordComponent;
        }
    }

    protected createInputs(errorDetailPreview: ErrorDetailPreview) {
        return {
            "errorId": errorDetailPreview.id,
            "onErrorUpdated": this.onErrorUpdated
        }
    }

    protected onErrorUpdated = (baseError: BaseError) => {
        const currentError: ErrorDetailPreview | undefined = this.errorsPagedResult.items.find(error => error.id == baseError.id);
        if (currentError) {
            currentError.priority = baseError.errorPriority;
            currentError.isSolved = baseError.isSolved;
        }
    }

    public async changePage(pageEvent: PageEvent) {
        const newQueryFilter = new LogBookQueryFilter(pageEvent.pageIndex, pageEvent.pageSize, this.queryFilter.logBookErrorFilter);
        this.queryFilterChanged.emit(newQueryFilter);
    }

    public solveAllErrors() {
        this.expandedError = undefined;
        this.lastClosedError = undefined;
        this.allErrorsSolved.emit();
    }

    public get pageSize(): number {
        return this.errorsPagedResult.pageSize;
    }

    public get currentPageIndex(): number {
        return this.errorsPagedResult.pageNumber - 1;
    }

    public get totalAmountOfErrorItems(): number {
        return this.errorsPagedResult.totalCount;
    }

    public get paginationDisabled(): boolean {
        return this.isLoading || this.totalAmountOfErrorItems == 0;
    }

    public filter() {
        const result = this.filterForm.getRawValue();
        const newLogbookErrorFilters = new LogBookErrorFilter(result.search.trim());

        const newQueryFilter = new LogBookQueryFilter(0, this.pageSize, newLogbookErrorFilters);

        this.closeFilterModal();
        this.queryFilterChanged.emit(newQueryFilter);
    }

    protected closeFilterModal() {
        this.filterModal()?.dismiss();
    }
}
